/**
 * QuestLogPanel -- HTML/CSS overlay panel for viewing and managing quests.
 *
 * Opened with J key / D-pad Up. Shows active quests on the left,
 * selected quest details on the right. Supports quest abandonment (side only),
 * quest tracking, and completed quest viewing.
 *
 * All elements use data-focusable for FocusManager navigation.
 *
 * @module QuestLogPanel
 */

import focusManager from '../../input/FocusManager.js';
import eventBus from '../../core/EventBus.js';
import questSystem from '../../systems/QuestSystem.js';
import debugLog from '../../debug/DebugLog.js';

/** Tabs in the quest log. */
const TABS = ['active', 'completed'];

export default class QuestLogPanel {
  /**
   * @param {object} [options]
   * @param {object} [options.party] - Party reference
   */
  constructor(options = {}) {
    this._party = options.party || null;

    /** @type {HTMLElement|null} Root panel element */
    this._el = null;

    /** @type {string} Current tab */
    this._currentTab = 'active';

    /** @type {string|null} Currently selected quest ID */
    this._selectedQuestId = null;

    /** @type {boolean} Whether the panel is open */
    this._isOpen = false;

    /** Event handler references */
    this._eventHandlers = {};

    /** @type {HTMLElement|null} Confirm dialog element */
    this._confirmEl = null;
  }

  /**
   * Toggle the panel open/closed.
   */
  toggle() {
    if (this._isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Open the quest log panel.
   */
  open() {
    if (this._isOpen) return;
    this._isOpen = true;

    this._build();
    this._render();
    this._subscribeEvents();

    // Push focus group
    if (this._el) {
      focusManager.pushGroup('quest-log', this._el);
    }

    debugLog.event('QuestLogPanel: opened');
  }

  /**
   * Close the quest log panel.
   */
  close() {
    if (!this._isOpen) return;
    this._isOpen = false;

    this._unsubscribeEvents();
    this._dismissConfirm();

    // Pop focus group
    focusManager.popGroup();

    // Remove DOM
    if (this._el && this._el.parentNode) {
      this._el.parentNode.removeChild(this._el);
    }
    this._el = null;

    debugLog.event('QuestLogPanel: closed');
  }

  /**
   * Check if the panel is currently open.
   * @returns {boolean}
   */
  isOpen() {
    return this._isOpen;
  }

  // ── DOM Construction ──────────────────────────────────

  _build() {
    const uiRoot = document.getElementById('ui-root');
    if (!uiRoot) return;

    // Remove existing
    if (this._el && this._el.parentNode) {
      this._el.parentNode.removeChild(this._el);
    }

    this._el = document.createElement('div');
    this._el.className = 'quest-log-panel';
    this._el.id = 'quest-log-panel';
    this._el.setAttribute('role', 'dialog');
    this._el.setAttribute('aria-label', 'Quest Log');

    // Listen for focus group cancel
    this._el.addEventListener('focusgroup:cancelled', () => {
      this.close();
    });

    uiRoot.appendChild(this._el);
  }

  _render() {
    if (!this._el) return;

    this._el.innerHTML = '';

    // Header
    const header = document.createElement('div');
    header.className = 'quest-log-header';

    const title = document.createElement('h2');
    title.className = 'quest-log-title';
    title.textContent = 'Quest Log';
    header.appendChild(title);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'quest-log-close';
    closeBtn.setAttribute('data-focusable', 'true');
    closeBtn.textContent = 'X';
    closeBtn.title = 'Close (Esc)';
    closeBtn.addEventListener('click', () => this.close());
    header.appendChild(closeBtn);

    this._el.appendChild(header);

    // Tabs
    const tabBar = document.createElement('div');
    tabBar.className = 'quest-log-tabs';

    for (const tab of TABS) {
      const tabBtn = document.createElement('button');
      tabBtn.className = 'quest-log-tab' + (this._currentTab === tab ? ' active' : '');
      tabBtn.setAttribute('data-focusable', 'true');
      tabBtn.setAttribute('data-tab', tab);
      tabBtn.textContent = tab === 'active' ? 'Active' : 'Completed';
      tabBtn.addEventListener('click', () => {
        this._currentTab = tab;
        this._selectedQuestId = null;
        this._render();
        focusManager.refreshGroup();
      });
      tabBar.appendChild(tabBtn);
    }

    this._el.appendChild(tabBar);

    // Content area (split layout)
    const content = document.createElement('div');
    content.className = 'quest-log-content';

    // Left: quest list
    const listPane = document.createElement('div');
    listPane.className = 'quest-log-list';
    this._renderQuestList(listPane);
    content.appendChild(listPane);

    // Right: quest details
    const detailPane = document.createElement('div');
    detailPane.className = 'quest-log-detail';
    this._renderQuestDetail(detailPane);
    content.appendChild(detailPane);

    this._el.appendChild(content);

    // Refresh focus manager to pick up new elements
    if (this._isOpen) {
      focusManager.refreshGroup();
    }
  }

  _renderQuestList(container) {
    const quests = this._currentTab === 'active'
      ? questSystem.getActiveQuests()
      : questSystem.getCompletedQuests();

    if (quests.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'quest-log-empty';
      empty.textContent = this._currentTab === 'active'
        ? 'No active quests. Explore the world to find adventures!'
        : 'No completed quests yet.';
      container.appendChild(empty);
      return;
    }

    for (const q of quests) {
      const card = document.createElement('div');
      card.className = 'quest-log-card' +
        (this._selectedQuestId === q.questId ? ' selected' : '') +
        (questSystem.getTrackedQuestId() === q.questId ? ' tracked' : '');
      card.setAttribute('data-focusable', 'true');
      card.setAttribute('data-quest-id', q.questId);

      // Type badge
      const badge = document.createElement('span');
      badge.className = `quest-badge quest-badge-${q.quest.type}`;
      badge.textContent = q.quest.type.charAt(0).toUpperCase() + q.quest.type.slice(1);
      card.appendChild(badge);

      // Title
      const titleEl = document.createElement('div');
      titleEl.className = 'quest-card-title';
      titleEl.textContent = q.quest.title;
      card.appendChild(titleEl);

      // Brief progress
      if (this._currentTab === 'active') {
        const step = q.quest.steps[q.currentStep];
        if (step) {
          const progressEl = document.createElement('div');
          progressEl.className = 'quest-card-progress';
          progressEl.textContent = step.text;
          card.appendChild(progressEl);
        }
      }

      card.addEventListener('click', () => {
        this._selectedQuestId = q.questId;
        this._render();
      });

      container.appendChild(card);
    }
  }

  _renderQuestDetail(container) {
    if (!this._selectedQuestId) {
      const placeholder = document.createElement('div');
      placeholder.className = 'quest-log-detail-placeholder';
      placeholder.textContent = 'Select a quest to view details';
      container.appendChild(placeholder);
      return;
    }

    const quests = this._currentTab === 'active'
      ? questSystem.getActiveQuests()
      : questSystem.getCompletedQuests();
    const questData = quests.find(q => q.questId === this._selectedQuestId);

    if (!questData) {
      container.textContent = 'Quest not found.';
      return;
    }

    const quest = questData.quest;

    // Title
    const titleEl = document.createElement('h3');
    titleEl.className = 'quest-detail-title';
    titleEl.textContent = quest.title;
    container.appendChild(titleEl);

    // Type badge
    const badge = document.createElement('span');
    badge.className = `quest-badge quest-badge-${quest.type}`;
    badge.textContent = quest.type.charAt(0).toUpperCase() + quest.type.slice(1);
    container.appendChild(badge);

    // Description
    const descEl = document.createElement('p');
    descEl.className = 'quest-detail-desc';
    descEl.textContent = quest.description;
    container.appendChild(descEl);

    // Objectives
    const objTitle = document.createElement('div');
    objTitle.className = 'quest-detail-section-title';
    objTitle.textContent = 'Objectives';
    container.appendChild(objTitle);

    const objList = document.createElement('ul');
    objList.className = 'quest-objectives';

    for (let i = 0; i < quest.steps.length; i++) {
      const step = quest.steps[i];
      const li = document.createElement('li');
      li.className = 'quest-objective';

      const currentStep = questData.currentStep !== undefined ? questData.currentStep : quest.steps.length;
      const isComplete = i < currentStep;
      const isCurrent = i === currentStep;

      if (isComplete) {
        li.classList.add('objective-complete');
      }
      if (isCurrent) {
        li.classList.add('objective-current');
      }

      // Checkbox
      const checkbox = document.createElement('span');
      checkbox.className = 'quest-objective-check';
      checkbox.textContent = isComplete ? '[x]' : '[ ]';
      li.appendChild(checkbox);

      // Text
      const text = document.createElement('span');
      text.className = 'quest-objective-text';
      text.textContent = step.text;
      li.appendChild(text);

      // Progress for kill quests
      if (isCurrent && step.type === 'kill' && step.targetCount) {
        const killCount = questData.killProgress?.[step.targetEnemyId] || 0;
        const progressText = document.createElement('span');
        progressText.className = 'quest-objective-progress';
        progressText.textContent = ` (${killCount}/${step.targetCount})`;
        li.appendChild(progressText);
      }

      objList.appendChild(li);
    }

    container.appendChild(objList);

    // Rewards preview
    if (quest.rewards) {
      const rewardTitle = document.createElement('div');
      rewardTitle.className = 'quest-detail-section-title';
      rewardTitle.textContent = 'Rewards';
      container.appendChild(rewardTitle);

      const rewardList = document.createElement('div');
      rewardList.className = 'quest-rewards-preview';

      if (quest.rewards.gold) {
        const goldEl = document.createElement('span');
        goldEl.className = 'quest-reward-item';
        goldEl.textContent = `${quest.rewards.gold} Gold`;
        rewardList.appendChild(goldEl);
      }
      if (quest.rewards.xp) {
        const xpEl = document.createElement('span');
        xpEl.className = 'quest-reward-item';
        xpEl.textContent = `${quest.rewards.xp} XP`;
        rewardList.appendChild(xpEl);
      }
      if (quest.rewards.items) {
        for (const itemId of quest.rewards.items) {
          const itemEl = document.createElement('span');
          itemEl.className = 'quest-reward-item';
          itemEl.textContent = itemId.replace(/_/g, ' ');
          rewardList.appendChild(itemEl);
        }
      }

      container.appendChild(rewardList);
    }

    // Action buttons (for active quests)
    if (this._currentTab === 'active') {
      const actions = document.createElement('div');
      actions.className = 'quest-detail-actions';

      // Track button
      const isTracked = questSystem.getTrackedQuestId() === this._selectedQuestId;
      const trackBtn = document.createElement('button');
      trackBtn.className = 'quest-action-btn quest-track-btn' + (isTracked ? ' tracked' : '');
      trackBtn.setAttribute('data-focusable', 'true');
      trackBtn.textContent = isTracked ? 'Tracking' : 'Track';
      trackBtn.addEventListener('click', () => {
        questSystem.setTrackedQuest(this._selectedQuestId);
        this._render();
      });
      actions.appendChild(trackBtn);

      // Abandon button (side quests only)
      if (quest.type !== 'main') {
        const abandonBtn = document.createElement('button');
        abandonBtn.className = 'quest-action-btn quest-abandon-btn';
        abandonBtn.setAttribute('data-focusable', 'true');
        abandonBtn.textContent = 'Abandon';
        abandonBtn.addEventListener('click', () => {
          this._showAbandonConfirm(this._selectedQuestId, quest.title);
        });
        actions.appendChild(abandonBtn);
      }

      container.appendChild(actions);
    }
  }

  // ── Abandon Confirmation ──────────────────────────────

  _showAbandonConfirm(questId, questTitle) {
    this._dismissConfirm();

    const uiRoot = document.getElementById('ui-root');
    if (!uiRoot) return;

    this._confirmEl = document.createElement('div');
    this._confirmEl.className = 'quest-confirm-overlay';
    this._confirmEl.id = 'quest-abandon-confirm';

    const box = document.createElement('div');
    box.className = 'quest-confirm-box';

    const msg = document.createElement('p');
    msg.className = 'quest-confirm-msg';
    msg.textContent = `Abandon "${questTitle}"? This cannot be undone.`;
    box.appendChild(msg);

    const btns = document.createElement('div');
    btns.className = 'quest-confirm-buttons';

    const yesBtn = document.createElement('button');
    yesBtn.className = 'quest-confirm-yes';
    yesBtn.setAttribute('data-focusable', 'true');
    yesBtn.textContent = 'Abandon';
    yesBtn.addEventListener('click', () => {
      questSystem.abandonQuest(questId);
      this._dismissConfirm();
      this._selectedQuestId = null;
      this._render();
    });
    btns.appendChild(yesBtn);

    const noBtn = document.createElement('button');
    noBtn.className = 'quest-confirm-no';
    noBtn.setAttribute('data-focusable', 'true');
    noBtn.textContent = 'Cancel';
    noBtn.addEventListener('click', () => {
      this._dismissConfirm();
    });
    btns.appendChild(noBtn);

    box.appendChild(btns);
    this._confirmEl.appendChild(box);
    uiRoot.appendChild(this._confirmEl);

    // Push a new focus group for the confirm dialog
    focusManager.pushGroup('quest-abandon-confirm', this._confirmEl);
  }

  _dismissConfirm() {
    if (this._confirmEl) {
      // Pop focus group if it's the confirm dialog
      if (focusManager.getGroupCount() > 0) {
        focusManager.popGroup();
      }
      if (this._confirmEl.parentNode) {
        this._confirmEl.parentNode.removeChild(this._confirmEl);
      }
      this._confirmEl = null;
    }
  }

  // ── Event Handling ────────────────────────────────────

  _subscribeEvents() {
    this._eventHandlers.onQuestProgress = () => {
      if (this._isOpen) this._render();
    };
    this._eventHandlers.onQuestComplete = () => {
      if (this._isOpen) this._render();
    };
    this._eventHandlers.onQuestStart = () => {
      if (this._isOpen) this._render();
    };

    eventBus.on('quest:progress', this._eventHandlers.onQuestProgress);
    eventBus.on('quest:complete', this._eventHandlers.onQuestComplete);
    eventBus.on('quest:start', this._eventHandlers.onQuestStart);
  }

  _unsubscribeEvents() {
    if (this._eventHandlers.onQuestProgress) {
      eventBus.off('quest:progress', this._eventHandlers.onQuestProgress);
    }
    if (this._eventHandlers.onQuestComplete) {
      eventBus.off('quest:complete', this._eventHandlers.onQuestComplete);
    }
    if (this._eventHandlers.onQuestStart) {
      eventBus.off('quest:start', this._eventHandlers.onQuestStart);
    }
    this._eventHandlers = {};
  }
}
